import { useEffect, useState } from 'react';
import * as ivyChatApi from '@/services/ivyChatApi';
import { subscribeToAuthState } from '@/services/firebase';
import { sessionBelongsTo } from '@/lib/ivyChatSession';

interface IvyChatAuthState {
  authReady: boolean;
  authError: string | null;
}

export function useIvyChatAuth(): IvyChatAuthState {
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Runs on every Firebase auth change, so signing in as another account re-mints the token.
    const unsub = subscribeToAuthState(async (user) => {
      if (cancelled) return;
      if (!user) {
        setAuthReady(false);
        setAuthError('Sign in to use Ivy Chat');
        return;
      }

      if (ivyChatApi.hasSessionToken()) {
        const valid = await ivyChatApi.validateSession();
        if (cancelled) return;
        if (valid && sessionBelongsTo(valid.user, user.email)) {
          setAuthReady(true);
          setAuthError(null);
          return;
        }
        ivyChatApi.clearSessionToken();
      }

      try {
        const firebaseToken = await user.getIdToken();
        const { sessionToken } = await ivyChatApi.exchangeFirebaseToken(
          firebaseToken,
          user.email || '',
          user.displayName || user.email || 'User',
        );
        ivyChatApi.setSessionToken(sessionToken);
        if (!cancelled) {
          setAuthReady(true);
          setAuthError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setAuthError((err as Error).message);
        }
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  return { authReady, authError };
}
